import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../service/Api';

const heroImage = encodeURI(
  `${process.env.PUBLIC_URL || ''}/WhatsApp Image 2026-04-18 at 08.45.04.jpeg`
);

const CompanyLogo = () => (
  <div className="flex items-center gap-3">
    <img
     // src="https://i.ibb.co/MvVKzjj/file-00000000a5dc7209bf802deb262dc9a8.png"
 src="https://i.ibb.co/0Rn8HPMr/heyparrot-logo-removebg-preview.png"
      alt="HeyParrot"
      className="h-12 w-auto sm:h-14"
      onError={(e) => {
        e.target.style.display = 'none';
      }}
    />
    <div className="hidden text-left sm:block">
      <p className="text-sm font-semibold uppercase tracking-[0.28em] text-[#C78052]">
        HeyParrot
      </p>
      <p className="text-sm text-[#5E6472]">Prospect-ready collateral, faster</p>
    </div>
  </div>
);

export default function App() {
  const { isInExtension, login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const url = new URLSearchParams(window.location.search).get('url');

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      setError('');
      const res = await apiService.googleLogin(credentialResponse.credential);

      if (res.data.success) {
        const { token, user, tenant_id } = res.data;

        if (tenant_id) {
          localStorage.setItem('aura_tenant_id', tenant_id);
          apiService.setTenantId(tenant_id);
        }

        localStorage.setItem('aura_token', token);
        apiService.setAuthToken(token);
        await login(token, user);

        if (isInExtension && !url) {
          navigate('/');
        } else if (isInExtension && url) {
          navigate(`/deal-insights?url=${encodeURIComponent(url)}`);
        } else {
          navigate('/tailspin/dashboard');
        }
      }
    } catch (err) {
      console.error('Google Login Error', err);
      setError(err.response?.data?.error || 'Google Login Failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLinkSubmit = async (e) => {
    e.preventDefault();

    if (!email || !email.trim()) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setMagicLinkLoading(true);
      setError('');
      setMagicLinkSent(false);

      await apiService.sendMagicLink(email, isInExtension);

      setMagicLinkSent(true);
      setEmail('');
    } catch (err) {
      console.error('Magic Link Error', err);
      setError(err.response?.data?.error || 'Failed to send magic link. Please try again.');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen overflow-hidden font-sans antialiased"
      style={{
        background:
          'radial-gradient(circle at top left, rgba(215,180,114,0.22), transparent 30%), linear-gradient(135deg, #F8F7F4 0%, #F3EDE2 45%, #FAF7F2 100%)',
      }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-gradient-to-b from-[#1D2B53]/8 to-transparent" />
      <div className="pointer-events-none absolute -left-16 top-24 h-64 w-64 rounded-full bg-[#D7B472]/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-10 bottom-16 h-72 w-72 rounded-full bg-[#1D2B53]/10 blur-3xl" />

      <div className="mx-auto flex min-h-screen w-full max-w-7xl items-center px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        <div className="grid w-full overflow-hidden rounded-[28px] border border-white/60 bg-white/70 shadow-[0_30px_90px_rgba(29,43,83,0.14)] backdrop-blur xl:grid-cols-[1.15fr_0.85fr] xl:rounded-[32px]">
          <section className="relative hidden min-h-[340px] overflow-hidden xl:block xl:min-h-[720px]">
            <img
              src={heroImage}
              alt="Decorative HeyParrot artwork"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-[linear-gradient(140deg,rgba(12,19,39,0.18)_0%,rgba(12,19,39,0.34)_36%,rgba(12,19,39,0.82)_100%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(215,180,114,0.28),transparent_24%)]" />

            <div className="relative flex h-full flex-col justify-between p-6 text-white sm:p-8 lg:p-10">
              <div className="flex items-center justify-between">
                <span className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-white/90 backdrop-blur">
                  AI Collateral Studio
                </span>
              </div>

              <div className="max-w-xl space-y-5">
                <div className="h-px w-16 bg-[#D7B472]" />
                <h1 className="text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
                  HeyParrot helps you generate targeted, high-quality collateral for your
                  prospects in minutes
                </h1>
                <p className="max-w-lg text-sm leading-6 text-white/78 sm:text-base">
                  Create polished prospect materials with the speed of AI and the clarity
                  your team needs to move opportunities forward.
                </p>
              </div>
            </div>
          </section>

          <section className="flex items-center justify-center px-4 py-7 sm:px-8 sm:py-10 lg:px-10 xl:px-12">
            <div className="w-full max-w-md">
              <header className="mb-8 space-y-6">
                <CompanyLogo />

                <div className="space-y-3">
                  <span className="inline-flex rounded-full bg-[#F4E7CF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#8F6235]">
                    Secure sign in
                  </span>
                  <div>
                    <h2 className="text-3xl font-semibold tracking-tight text-[#0A0A10]">
                      Welcome back
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-[#5E6472]">
                      Sign in to access your workspace and start generating prospect-ready
                      collateral.
                    </p>
                    <p className="text-sm leading-6 text-[#6B7280] xl:hidden">
                      Generate targeted, high-quality collateral for your prospects in
                      minutes.
                    </p>
                  </div>
                </div>
              </header>

              <div className="rounded-[28px] border border-[#EADCC2] bg-white p-6 shadow-[0_18px_60px_rgba(10,10,16,0.08)] sm:p-7">
                <div className="space-y-6">
                  <div className="flex justify-center">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => {
                        setError('Google Login Failed');
                      }}
                      useOneTap
                      theme="filled_blue"
                      shape="pill"
                      width="360"
                    />
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-[#E7D4AE]" />
                    </div>
                    <div className="relative flex justify-center text-xs font-medium uppercase tracking-[0.2em] text-[#8A8F98]">
                      <span className="bg-white px-3">Or continue with email</span>
                    </div>
                  </div>

                  <form onSubmit={handleMagicLinkSubmit} className="space-y-4">
                    <div>
                      <label
                        htmlFor="email"
                        className="mb-2 block text-sm font-medium text-[#2A2D34]"
                      >
                        Work email
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="name@company.com"
                        className="w-full rounded-2xl border border-[#DCC293] bg-[#FCFAF6] px-4 py-3 text-[#0A0A10] outline-none transition-all placeholder:text-[#9EA3AE] focus:border-[#C78052] focus:ring-4 focus:ring-[#D7B472]/20"
                        disabled={magicLinkLoading}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={magicLinkLoading || !email}
                      className="flex w-full items-center justify-center rounded-2xl px-4 py-3.5 text-sm font-semibold text-white transition-all"
                      style={{
                        background:
                          magicLinkLoading || !email
                            ? 'linear-gradient(135deg, rgba(199,128,82,0.6), rgba(29,43,83,0.65))'
                            : 'linear-gradient(135deg, #C78052 0%, #1D2B53 100%)',
                        boxShadow:
                          magicLinkLoading || !email
                            ? 'none'
                            : '0 18px 36px rgba(29, 43, 83, 0.18)',
                        cursor: magicLinkLoading || !email ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {magicLinkLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending secure link...
                        </>
                      ) : (
                        'Email me a magic link'
                      )}
                    </button>
                  </form>

                  {magicLinkSent && (
                    <div className="rounded-2xl border border-[#C6E2CB] bg-[#EDF8F0] p-4 text-center text-sm font-medium text-[#245A35]">
                      Magic link sent. Check your inbox to continue securely.
                    </div>
                  )}

                  {error && (
                    <div className="rounded-2xl border border-[#FFD1CA] bg-[#FFF4F2] p-4 text-center text-sm font-medium text-[#C14B3C]">
                      {error}
                    </div>
                  )}

                  {loading && (
                    <div className="flex items-center justify-center text-sm text-[#5E6472]">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Logging you in...
                    </div>
                  )}

                  <p className="text-center text-xs leading-5 text-[#8A8F98]">
                    By continuing, you agree to a secure authentication flow for your
                    HeyParrot workspace.
                  </p>
                </div>
              </div>

              <footer className="mt-6 text-center text-xs text-[#7A7F89]">
                &copy; {new Date().getFullYear()} HeyParrot. All rights reserved.
              </footer>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
